import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type {
  MemberCohort,
  MemberSeat,
  SeatSet,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import {
  isActiveSeat,
  isLiveSeat,
  isMovableKind,
  isSeatKind,
  REVOCABLE_SEAT_KINDS,
  SEAT_KIND,
  type SeatKind,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

const { seatPicker, revokeDialog } = appStrings.modules.coachingDashboard.seatActions;

const KIND_LABELS: Readonly<Record<string, string>> = revokeDialog.pickerKindLabels;

/** A member paired with what the row said about their seats — which may be that it did not
 * say. */
export type SeatHolder = Readonly<{
  id: number;
  name: string;
  seats: SeatSet;
  cohorts?: readonly MemberCohort[];
}>;

/**
 * The seats a holder is KNOWN to hold. Answers nothing about the unknown case: callers that
 * can encounter one must branch on `known` first, and the doors do, which is why everything
 * downstream of an open door may use this.
 */
export const knownSeats = (set: SeatSet): readonly MemberSeat[] => (set.known ? set.seats : []);

/** Seats of one kind that have not ended. Named once because two questions ask it — which
 * seats a door may act on, and which offerings someone is on — and they must not drift
 * apart on what counts as still holding a seat. */
const liveSeatsOfKind = (seats: readonly MemberSeat[], kind: SeatKind): MemberSeat[] =>
  seats.filter((seat) => isLiveSeat(seat.status) && isSeatKind(seat.offering_kind, kind));

/**
 * Each door's own eligibility, mirroring the refusal the server would raise. Kept apart
 * rather than collapsed into one predicate: a live seat that is not active can be ended
 * but not moved, and folding the two would withhold a door that works.
 *
 * A row offers revoke on the KIND its tab acts on. The reads carry every rostering kind,
 * ordered by institution and roster name rather than kind, so someone who coaches and
 * studies at one institution arrives with both seats in no particular order.
 */
export const revocableSeats = liveSeatsOfKind;

/** Every seat the revoke door can end, whatever its kind; the operator picks one. */
export const allRevocableSeats = (seats: readonly MemberSeat[]): MemberSeat[] =>
  seats.filter(
    (seat) =>
      isLiveSeat(seat.status) &&
      REVOCABLE_SEAT_KINDS.some((kind) => isSeatKind(seat.offering_kind, kind)),
  );

export const activeSeatsOfKind = (seats: readonly MemberSeat[], kind: SeatKind): MemberSeat[] =>
  seats.filter((seat) => isActiveSeat(seat.status) && isSeatKind(seat.offering_kind, kind));

export const movableSeats = (seats: readonly MemberSeat[]): MemberSeat[] =>
  seats.filter((seat) => isActiveSeat(seat.status) && isMovableKind(seat.offering_kind));

/** A batch moves the seats of the tab's kind only; a selected person's other seats stay put. */
export const movableSeatsOfKind = (seats: readonly MemberSeat[], kind: SeatKind): MemberSeat[] =>
  movableSeats(seats).filter((seat) => isSeatKind(seat.offering_kind, kind));

/** Cohorts sit on student rosters only, which is narrower than what the move door takes. */
export const cohortSeats = (seats: readonly MemberSeat[]): MemberSeat[] =>
  activeSeatsOfKind(seats, SEAT_KIND.student);

export const rosterLabel = (seat: MemberSeat): string =>
  seat.roster_name ?? seatPicker.unnamedRoster(seat.roster_id);

/**
 * The offerings a member holds a live seat on, one entry per distinct name. A roster has
 * no name of its own — it carries its offering's — so the live seat set IS the program
 * list, and an ended seat is not a program they are on. Live rather than active, matching
 * the revoke door: an unclaimed invitation would read here as a place held, which the two
 * reads never exercise because they carry live actives only.
 */
export const seatedPrograms = (seats: readonly MemberSeat[], kind: SeatKind): string[] =>
  Array.from(new Set(liveSeatsOfKind(seats, kind).map(rosterLabel)));

export type SeatedInstitution = Readonly<{ id: number; name: string }>;

/** The institutions running the rosters a holder has a live seat of one kind on, once each,
 * by name. An unknown seat set yields none; it does not say the holder sits nowhere. */
export const seatedInstitutions = (set: SeatSet, kind: SeatKind): SeatedInstitution[] => {
  const byId = new Map<number, SeatedInstitution>();

  liveSeatsOfKind(knownSeats(set), kind).forEach(({ managing_institution: institution }) => {
    if (!institution) return;

    byId.set(institution.id, {
      id: institution.id,
      name: institution.name ?? seatPicker.unnamedOperator(institution.id),
    });
  });

  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const operatorLabel = (seat: MemberSeat): string => {
  const operator = seat.managing_institution;

  if (operator === null || operator === undefined) return seatPicker.unknownOperator;

  return operator.name ?? seatPicker.unnamedOperator(operator.id);
};

/**
 * A roster carries its offering's name, which repeats across operators, so the operator
 * is named alongside it as soon as the set spans more than one — naming it always would
 * be noise in the single-operator case, which is most of them.
 */
export const seatOptionLabel = (seat: MemberSeat, seats: readonly MemberSeat[]): string => {
  const operators = new Set(seats.map((row) => row.managing_institution?.id ?? null));

  return operators.size > 1
    ? seatPicker.scopedOption(rosterLabel(seat), operatorLabel(seat))
    : seatPicker.option(rosterLabel(seat));
};

export const seatKindLabel = (offeringKind: string | null): string => {
  const kind = String(offeringKind);

  return KIND_LABELS[kind] ?? kind;
};

/** The picker option for a door whose seats can span kinds, so the kind is named too. */
export const seatWithKindLabel = (seat: MemberSeat, seats: readonly MemberSeat[]): string =>
  revokeDialog.option(seatOptionLabel(seat, seats), seatKindLabel(seat.offering_kind));

/** A seat paired with whose it is, so a report can name both. */
export type SeatContext = Readonly<{ holder: SeatHolder; seat: MemberSeat }>;

/** Which student each seat id belongs to, and which seat it is: a report names people
 * rather than rows, counts them the same way, and distinguishes one person's seats. */
export const seatContextBySeatId = (holders: readonly SeatHolder[]): Map<number, SeatContext> =>
  new Map(
    holders.flatMap((holder) =>
      knownSeats(holder.seats).map((seat) => [seat.seat_id, { holder, seat }] as const),
    ),
  );

/**
 * Holders that contribute no id to the batch. They are reported rather than dropped: a
 * request that never carried them comes back with nothing to say about them, and silence
 * would read as success.
 */
export const holdersWithoutEligibleSeat = (
  holders: readonly SeatHolder[],
  eligible: (seats: readonly MemberSeat[]) => MemberSeat[],
): SeatHolder[] => holders.filter((holder) => eligible(knownSeats(holder.seats)).length === 0);

export const eligibleSeatIds = (
  holders: readonly SeatHolder[],
  eligible: (seats: readonly MemberSeat[]) => MemberSeat[],
): number[] =>
  holders.flatMap((holder) => eligible(knownSeats(holder.seats)).map((seat) => seat.seat_id));
